import { Component, OnInit, OnChanges, SimpleChanges, Input, EventEmitter, Output, ViewChild, ChangeDetectorRef, Optional } from "@angular/core";
import { AzureMapConfig } from "../../models/azure-map-config";
import { Address, Location } from "../../../core/core";
import { AzureMapComponent } from "../azure-map/azure-map.component";
import { AzureMapService } from "../../services/azure-map.service";

@Component({
  selector: "common-location-typeahead-map",
  templateUrl: "./location-typeahead-map.component.html",
  styleUrls: ["./location-typeahead-map.component.scss"],
})
export class LocationTypeaheadMapComponent implements OnInit, OnChanges {
  @Input() location: Location;

  @Input() placeHolderText: string = "Enter City";
  @Input() showIcon: boolean = false;
  @Input() width: string; // must set parent form-group width for this to work
  @Input() onlyAddresses: boolean = false;
  @Input() dropdownClass: string = "";
  @Input() valid: boolean = true;
  @Input() addressText: string = "Jobsite";
  @Input() reverseGeoCodeInCoordinates: boolean = false; // if clickToAddMarker is true, then you can set this to true and it will attempt to get an address from the coordinates
  @Input() allowPartialAddress: boolean = false; // if true, only a city and state are required for an address to be returned by the reverse geocode
  @Input() isInModal: boolean = false;
  @Input() displayCityInCoordinates: boolean = false;
  @Input() disableMapInteraction: boolean = false; // New input to disable map and coordinate interaction
  /**
   * When true, the map is only rendered after the user clicks
   * "Don't have an address?". They drop a pin on the map — lat/lng
   * fields are hidden. Defaults to false so Admin/owner location
   * screens still show the map and optional coordinate inputs.
   */
  @Input() hideMapUntilCoordinates: boolean = false;
  /**
   * When the user opens coordinates without a picked address, center the map
   * here (e.g. the marketplace search-location dropdown). Does not count as
   * a selected jobsite.
   */
  @Input() defaultMapLocation: Location;

  @Output() locationSelected = new EventEmitter<Location>();
  @ViewChild("azureMapComponent") azureMapComponent: AzureMapComponent;

  useCoordinates: boolean = false;
  /** Lat/lng inputs stay on Admin/owner screens. Cart/add-to-cart only shows the map. */
  get showCoordinateFields(): boolean {
    return this.useCoordinates && !this.hideMapUntilCoordinates;
  }
  mapConfig = new AzureMapConfig();

  longitude: string = "";
  latitude: string = "";
  /** Stable array so az-map ngOnChanges does not fire (and pan) on every CD cycle. */
  mapLocations: Location[] = [];

  /** Street-level zoom once a search result or pin is known. Default map zoom is USA-wide. */
  /** Two clicks out from street-level (13) so the pin has neighborhood context. */
  private static readonly SELECTED_LOCATION_ZOOM = 11;

  constructor(private mapService: AzureMapService, @Optional() private changeDetector?: ChangeDetectorRef) {}

  ngOnInit() {
    this.mapConfig.zoom = 3;
    this.applyLocationMode(this.location);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.location || changes.hideMapUntilCoordinates) {
      this.applyLocationMode(this.location);
    }
  }

  /**
   * Cart/add-to-cart: if they dropped a pin and reverse-geocode did not
   * produce a street address, open the map by default. A typed or decoded
   * street stays on the address field.
   */
  private applyLocationMode(location: Location) {
    if (!location) {
      this.location = new Location();
      this.mapLocations = [this.location];
      return;
    }

    const mapAlreadyOpen = this.useCoordinates;
    this.location = location;
    const coordsOnly = this.hasCoordinates(location) && !this.hasResolvedStreetAddress(location);
    if (this.hideMapUntilCoordinates) {
      this.useCoordinates = coordsOnly;
    } else {
      this.useCoordinates = location.address === null || location.address?.empty === true;
    }
    this.mapLocations = [this.location];

    if (this.hasCoordinates(location)) {
      this.longitude = location.longitude.toString();
      this.latitude = location.latitude.toString();
      // Parent round-trips after a map click; keep the user's zoom in that case.
      this.centerMapOnLocation(location, { preserveZoom: mapAlreadyOpen });
    }
  }

  private hasResolvedStreetAddress(location: Location): boolean {
    const address = location?.address;
    return !!(
      address?.address1?.trim() &&
      address?.city?.trim() &&
      address?.stateProvince?.trim() &&
      address?.postalCode?.trim()
    );
  }

  /** Map click: pan to the pin and keep the current zoom. */
  onMapLocationSelected(location: Location) {
    this.locationIsSelected(location, { preserveZoom: true });
  }

  locationIsSelected(location: Location, options?: { preserveZoom?: boolean }) {
    if (location === null || !location?.latitude || !location?.longitude) {
      return;
    }

    if (!location.address?.address1 || !location.address?.city || !location.address?.stateProvince || !location.address?.postalCode) {
      location.address = new Address();
      this.switchToCoordinates();
    } else {
      this.switchToAddress();
    }

    this.location = this.withNumericCoordinates(location);
    this.latitude = this.location.latitude.toString();
    this.longitude = this.location.longitude.toString();
    this.mapLocations = [this.location];
    this.centerMapOnLocation(this.location, options);
    this.locationSelected.emit(this.location);
  }

  switchToCoordinates() {
    if (this.disableMapInteraction) {
      return;
    }

    const alreadyOpen = this.useCoordinates;
    this.useCoordinates = true;
    this.changeDetector?.detectChanges();
    if (!alreadyOpen) {
      this.focusMapForCoordinateMode();
    }
  }

  /** Jobsite pin if they picked one; otherwise the search-results area. */
  private focusMapForCoordinateMode() {
    if (this.hasCoordinates(this.location)) {
      this.centerMapOnLocation(this.location);
      return;
    }
    this.centerMapOnArea(this.defaultMapLocation);
  }

  /** Move the camera without treating this as the selected jobsite. */
  centerMapOnArea(location: Location) {
    const normalized = this.withNumericCoordinates(location);
    if (!normalized) {
      return;
    }

    this.mapConfig.zoom = LocationTypeaheadMapComponent.SELECTED_LOCATION_ZOOM;
    this.mapConfig.center = [normalized.longitude, normalized.latitude];
    if (this.azureMapComponent) {
      this.azureMapComponent.jumpToFirstLocation();
    }
  }

  hasCoordinates(location: Location): boolean {
    const latitude = Number(location?.latitude);
    const longitude = Number(location?.longitude);
    return Number.isFinite(latitude) && Number.isFinite(longitude);
  }

  centerMapOnLocation(location: Location, options?: { preserveZoom?: boolean }) {
    const normalized = this.withNumericCoordinates(location);
    if (!normalized) {
      return;
    }

    this.mapConfig.center = [normalized.longitude, normalized.latitude];

    if (options?.preserveZoom) {
      const currentZoom = this.currentMapZoom();
      if (currentZoom != null) {
        this.mapConfig.zoom = currentZoom;
      }
      if (this.azureMapComponent) {
        this.azureMapComponent.locations = [normalized];
        this.azureMapComponent.panToCenter(this.mapConfig.center);
      }
      return;
    }

    this.mapConfig.zoom = LocationTypeaheadMapComponent.SELECTED_LOCATION_ZOOM;
    if (this.azureMapComponent) {
      this.azureMapComponent.locations = [normalized];
      this.azureMapComponent.jumpToFirstLocation();
      this.azureMapComponent.onLocationChange();
    }
  }

  private currentMapZoom(): number | null {
    const zoom = Number(this.azureMapComponent?.map?.getCamera?.()?.zoom);
    return Number.isFinite(zoom) ? zoom : null;
  }

  private withNumericCoordinates(location: Location): Location {
    if (!this.hasCoordinates(location)) {
      return null;
    }

    location.latitude = Number(location.latitude);
    location.longitude = Number(location.longitude);
    return location;
  }

  switchToAddress() {
    if (!this.disableMapInteraction) {
      this.useCoordinates = false;
    }
  }

  latitudeChange(latitude: string) {
    if (this.disableMapInteraction) {
      return;
    }

    this.latitude = latitude;
    if (!this.latitude?.length || !this.longitude?.length) {
      this.locationSelected.emit(null);
      return;
    }

    this.updateLocationByCoordinates(latitude, this.longitude);
  }

  longitudeChange(longitude: string) {
    if (this.disableMapInteraction) {
      return;
    }

    this.longitude = longitude;
    if (!this.latitude?.length || !this.longitude?.length) {
      this.locationSelected.emit(null);
      return;
    }

    this.updateLocationByCoordinates(this.latitude, longitude);
  }

  updateLocationByCoordinates(latitude: string, longitude: string) {
    if (!this.location) {
      this.location = new Location();
    }

    this.location.longitude = parseFloat(longitude);
    this.location.latitude = parseFloat(latitude);

    if (this.reverseGeoCodeInCoordinates) {
      this.mapService.getReversedLocation(this.location, this.allowPartialAddress).subscribe(locationReversed => {
        if (!locationReversed.address) {
          locationReversed.address = new Address();
        }
        this.location = locationReversed;
        this.mapLocations = [this.location];
        this.azureMapComponent.locations = [this.location];
        this.azureMapComponent.onLocationChange();
        this.locationSelected.emit(this.location);
      });
    } else {
      this.location.address = new Address();
      this.mapLocations = [this.location];
      this.azureMapComponent.locations = [this.location];
      this.azureMapComponent.onLocationChange();
      this.locationSelected.emit(this.location);
    }
  }
}